import { FILE_STATUS, seriesColumnList } from '../constants';
import type {
  HealthSystemOption,
  HealthSystemSummaryRow,
  JobSummaryRow,
  PurposeOfUseOption,
  RequestRow,
  StatusCount,
} from '../interfaces/Dashboard';

/**
 * Sample data for local UI validation, served instead of API responses when
 * `VITE_USE_FIXTURES=true` under the dev server (see `useFixtures()`).
 *
 * This exists because the AR connector cannot be reached from a developer
 * machine: the legacy public GCP hosts are gone and the current connector sits on
 * a private VNet address behind DataNovaProxy. Rather than leave every page empty
 * until Part 2 wires up that proxy, these fixtures exercise the real components —
 * charts, filters, pagination, the job-summary modal and CSV export — with data
 * shaped exactly like the connector's.
 *
 * Deliberately deterministic (no randomness) so repeated runs are comparable.
 */

const HEALTH_SYSTEM_NAMES = [
  'Northside Regional Health',
  'Lakeview Medical Group',
  'Cedar Valley Hospital',
  'Harborview Clinics',
  'Summit Care Network',
  'Riverbend Health System',
  'Oakfield Physicians',
  'Pine Ridge Medical',
];

const PURPOSE_OF_USE_NAMES = [
  'Treatment',
  'Payment',
  'Operations',
  'Legal Request',
];

/** Small deterministic spread so bars differ per row without randomness. */
const pseudoCount = (rowIndex: number, seriesIndex: number): number => {
  const base = (rowIndex + 2) * (seriesIndex + 3);
  return (base * 7) % 47;
};

export const fixtureHealthSystems = (): HealthSystemOption[] =>
  HEALTH_SYSTEM_NAMES.slice(0, 4).map((name, index) => {
    const facilityid = String(4100 + index);
    return {
      facilityid,
      name,
      value: facilityid,
      label: `${name} - ${facilityid}`,
    };
  });

export const fixturePurposeOfUses = (): PurposeOfUseOption[] => [
  { value: 'All', label: 'All' },
  ...PURPOSE_OF_USE_NAMES.map((documenttype, index) => ({
    documenttypeid: String(index + 1),
    documenttype,
    value: String(index + 1),
    label: documenttype,
  })),
];

/**
 * Summary rows carrying one numeric key per status series, plus the
 * "Name (total)" shape the real endpoint returns.
 */
export const fixtureSummaryRows = (): HealthSystemSummaryRow[] =>
  HEALTH_SYSTEM_NAMES.map((name, rowIndex) => {
    const row: HealthSystemSummaryRow = { healthSystemName: name };

    let total = 0;
    seriesColumnList.forEach((column, seriesIndex) => {
      const count = pseudoCount(rowIndex, seriesIndex);
      row[column.field] = count;
      total += count;
    });

    row.totalCount = total;
    row.healthSystemName = `${name} (${total})`;
    return row;
  });

/** Per-status counts for the Dashboard donut. */
export const fixtureStatusCounts = (): StatusCount[] =>
  seriesColumnList.map((column, seriesIndex) => ({
    name: column.field,
    count: pseudoCount(1, seriesIndex) + 5,
  }));

const ALL_STATUS_IDS = Object.values(FILE_STATUS).flat();

/**
 * 137 rows so server-side pagination is genuinely exercised at the default page
 * size of 100 — the count is intentionally not a multiple of it.
 */
const ALL_REQUESTS: RequestRow[] = Array.from({ length: 137 }, (_, index) => ({
  id: index + 1,
  requesterrefid: `ROI-${String(90000 + index)}`,
  vendorrequestid: `VEN-${String(50000 + index)}`,
  requestid: `REQ-${String(10000 + index)}`,
  updatedat: `2026-08-${String((index % 28) + 1).padStart(2, '0')} 09:${String(
    index % 60,
  ).padStart(2, '0')}:00`,
  purposeofuse: PURPOSE_OF_USE_NAMES[index % PURPOSE_OF_USE_NAMES.length],
  filestatus: ALL_STATUS_IDS[index % ALL_STATUS_IDS.length],
  filepath: index % 3 === 0 ? `/documents/${index + 1}.pdf` : undefined,
  showJobSummaryLoader: false,
}));

/** Applies paging the way the real endpoint does, returning the total too. */
export const fixtureRequests = (
  page: number,
  rowsPerPage: number,
): { requestList: RequestRow[]; totalRequests: number } => ({
  requestList: ALL_REQUESTS.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  ),
  totalRequests: ALL_REQUESTS.length,
});

export const fixtureJobSummary = (
  requestid: string,
  requesterrefid: string,
): JobSummaryRow[] =>
  Array.from({ length: 7 }, (_, index) => ({
    id: index + 1,
    requesterrefid,
    requestid,
    errorresponse:
      index % 3 === 0
        ? 'HTTP 404 — Patient resource not found at the endpoint'
        : 'HTTP 200 — OK',
    logsummary: `Step ${index + 1} of 7 completed for ${requestid}`,
    resourceurl: `https://fhir.example.org/Patient/${1000 + index}`,
  }));

/** Simulates network latency so loading states are actually visible. */
export const fixtureDelay = (ms = 350): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
